/**
 * Cifras del tablero y avisos de carga de trabajo.
 *
 * Se calcula sobre TODAS las tareas (no sobre las filtradas): los filtros cambian lo que se ve,
 * no las cifras de fondo.
 */
import { LIMITE_EN_CURSO_POR_PERSONA, esEstadoCerrado, type Tarea } from '@cdtec-backlog/contrato';
import { nombreCorto } from '../../componentes/Avatar';
import { Esqueleto } from '../../componentes/Esqueleto';
import { useEtiquetaPrioridad } from '../configuracion/hooks';
import './ResumenTablero.css';

/** Personas con más tareas «En curso» que el límite acordado: devuelve nombre y cantidad. */
export function personasSobreElLimite(tareas: Tarea[]): { nombre: string; cantidad: number }[] {
  const cuenta = new Map<string, number>();
  for (const tarea of tareas) {
    if (tarea.estado !== 'EN_CURSO' || !tarea.responsable) continue;
    cuenta.set(tarea.responsable.nombre, (cuenta.get(tarea.responsable.nombre) ?? 0) + 1);
  }
  return [...cuenta.entries()]
    .filter(([, cantidad]) => cantidad > LIMITE_EN_CURSO_POR_PERSONA)
    .map(([nombre, cantidad]) => ({ nombre, cantidad }));
}

const ETIQUETAS_FIJAS = ['Abiertas', 'P0', 'En curso', 'Por aclarar', 'Esperan el OK a producción'];

/**
 * Primera carga: cinco tarjetas fantasma del mismo alto (`aria-busy`; el texto para lectores lo pone
 * la lista de tareas, que carga a la vez). `mostrar` llega a los ~300 ms.
 */
export function ResumenCargando({ mostrar }: { mostrar: boolean }) {
  return (
    <div className={`resumen${mostrar ? '' : ' carga--espera'}`} aria-busy="true">
      {ETIQUETAS_FIJAS.map((etiqueta) => (
        <div
          key={etiqueta}
          className="resumen__dato resumen__dato--fantasma esq-caja"
          aria-hidden="true"
        >
          <Esqueleto forma="numero" />
          <Esqueleto ancho={60} />
        </div>
      ))}
    </div>
  );
}

/** `tareas` en `null`: no se pudieron cargar; las cifras se muestran como «—». */
export function ResumenTablero({ tareas }: { tareas: Tarea[] | null }) {
  const etiquetaPrioridad = useEtiquetaPrioridad();
  if (tareas === null) {
    return (
      <div className="resumen">
        {ETIQUETAS_FIJAS.map((etiqueta) => (
          <div key={etiqueta} className="resumen__dato">
            <strong>—</strong>
            <span>{etiqueta === 'P0' ? etiquetaPrioridad('P0') : etiqueta}</span>
          </div>
        ))}
      </div>
    );
  }
  // Abiertas = ni hechas ni descartadas.
  const abiertas = tareas.filter((t) => !esEstadoCerrado(t.estado));
  const cuantas = (condicion: (t: Tarea) => boolean) => abiertas.filter(condicion).length;
  const urgentes = cuantas((t) => t.prioridad === 'P0');
  const esperaOk = cuantas((t) => t.estado === 'PARA_PRODUCCION');

  const datos: { etiqueta: string; valor: number; alerta?: boolean }[] = [
    { etiqueta: 'Abiertas', valor: abiertas.length },
    { etiqueta: etiquetaPrioridad('P0'), valor: urgentes, alerta: urgentes > 0 },
    { etiqueta: 'En curso', valor: cuantas((t) => t.estado === 'EN_CURSO') },
    { etiqueta: 'Por aclarar', valor: cuantas((t) => t.estado === 'POR_ACLARAR') },
    { etiqueta: 'Esperan el OK a producción', valor: esperaOk, alerta: esperaOk > 0 },
  ];

  return (
    <>
      <div className="resumen" aria-live="polite">
        {datos.map((dato) => (
          <div
            key={dato.etiqueta}
            className={`resumen__dato${dato.alerta ? ' resumen__dato--alerta' : ''}`}
          >
            <strong>{dato.valor}</strong>
            <span>{dato.etiqueta}</span>
          </div>
        ))}
      </div>
      {personasSobreElLimite(tareas).map(({ nombre, cantidad }) => (
        <p key={nombre} className="aviso-carga" title={nombre}>
          {nombreCorto(nombre)} tiene {cantidad} tareas en curso; el límite acordado es{' '}
          {LIMITE_EN_CURSO_POR_PERSONA}. Conviene terminar una antes de empezar otra.
        </p>
      ))}
    </>
  );
}
